import axios from "../utils/axios";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { normalizeListDurationForQuery } from "../utils/listTimePeriod";

const normalizeMerchantStatus = (status) => String(status || "").toLowerCase();
const isBlockedMerchantStatus = (status) =>
  ["deleted", "disabled", "declined", "rejected"].includes(normalizeMerchantStatus(status));
const isPreferredMerchantStatus = (status) =>
  ["approved", "live"].includes(normalizeMerchantStatus(status));
const getMerchantId = (merchant) => merchant?._id || merchant?.id || null;
const getMergedMerchantProfiles = (state) => [
  ...(state?.merchantReducer?.merchantProfiles?.data || []),
  ...(state?.authReducer?.tokenData?.user?.merchantProfiles || []),
].filter(Boolean);

const getFallbackMerchantId = (state) => {
  const merchantProfiles = getMergedMerchantProfiles(state);
  const preferredProfile = merchantProfiles.find((profile) => isPreferredMerchantStatus(profile?.status));
  if (preferredProfile) return getMerchantId(preferredProfile);

  const fallbackProfile = merchantProfiles.find((profile) => !isBlockedMerchantStatus(profile?.status));
  return getMerchantId(fallbackProfile);
};

const resolveMerchantId = (state) => {
  const selectedMerchant = state?.merchantReducer?.selectedMerchent;
  const selectedMerchantId = getMerchantId(selectedMerchant);
  if (selectedMerchantId && !isBlockedMerchantStatus(selectedMerchant?.status)) return selectedMerchantId;

  const storedMerchantId =
    typeof localStorage !== "undefined"
      ? localStorage.getItem("swoodle_selected_merchant_id")
      : null;

  const mergedProfiles = getMergedMerchantProfiles(state);

  if (storedMerchantId) {
    const storedProfile = mergedProfiles.find((profile) => getMerchantId(profile) === storedMerchantId);
    if (storedProfile && !isBlockedMerchantStatus(storedProfile?.status)) {
      return getMerchantId(storedProfile);
    }
  }

  return getFallbackMerchantId(state);
};

const getMerchantErrorPayload = (error, fallbackMessage) => {
  const rawMessage =
    error?.response?.data?.message ||
    error?.response?.data ||
    error?.message ||
    fallbackMessage ||
    "Request failed";
  const message = typeof rawMessage === "string" ? rawMessage : fallbackMessage || "Request failed";
  const normalized = String(message).toLowerCase();
  const blockedMerchant =
    normalized.includes("disabled") ||
    normalized.includes("deleted") ||
    normalized.includes("merchant account");

  return {
    success: false,
    message,
    blockedMerchant,
  };
};

export const getOfferMerchants = createAsyncThunk(
  "offer/merchants",
  async (thunkAPI) => {
    try {
      const response = await axios.get(`merchant?limit=${100}`);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const getMerchantProfiles = createAsyncThunk(
  "merchat/profiles",
  async (payload, thunkAPI) => {

    try {
      const response = await axios.get(`merchant/profiles/${payload?.id}`);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);


export const getMerchants = createAsyncThunk(
  "merchants",
  async (payload, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams();
      if (payload?.page) queryParams.append("page", payload.page);
      if (payload?.limit) queryParams.append("limit", payload.limit);
      if (payload?.search) queryParams.append("search", payload.search);
      if (payload?.contactSearch) queryParams.append("contactSearch", payload.contactSearch);
      if (payload?.categoryId) queryParams.append("categoryId", payload.categoryId);
      if (payload?.status) queryParams.append("status", payload.status);
      const timeValue = normalizeListDurationForQuery(payload?.duration || payload?.timePeriod || payload?.dateFilter);
      if (timeValue) {
        queryParams.append("duration", timeValue);
      }
      if (payload?.startDate) queryParams.append("startDate", payload.startDate);
      if (payload?.endDate) queryParams.append("endDate", payload.endDate);
      const queryString = queryParams.toString();
      const url = queryString ? `merchant?${queryString}` : "merchant";
      const response = await axios.get(url);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const getFundraisers = createAsyncThunk(
  "fundraiser",
  async (payload, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams();

      // Add all available parameters to the query string
      if (payload?.page) queryParams.append("page", payload.page);
      if (payload?.limit) queryParams.append("limit", payload.limit);
      if (payload?.search) queryParams.append("search", payload.search);
      if (payload?.status) queryParams.append("status", payload.status);
      const timeValue = normalizeListDurationForQuery(payload?.duration || payload?.timePeriod || payload?.dateFilter);
      if (timeValue) queryParams.append("duration", timeValue);
      if (payload?.startDate) queryParams.append("startDate", payload.startDate);
      if (payload?.endDate) queryParams.append("endDate", payload.endDate);

      // Build the final URL with all query parameters
      const queryString = queryParams.toString();
      const url = queryString ? `fundraiser/all?${queryString}` : "fundraiser";
      const response = await axios.get(url);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const getMerchantInquiries = createAsyncThunk(
  "inquiry/merchant",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.get(
        `inquiry/merchant?page=${payload?.page}`
      );
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const toggleMerchantStatus = createAsyncThunk(
  "merchant/status",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.post(`merchant/status/${payload?.id}`, {
        reason: payload?.reason,
      });
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const redeemOffer = createAsyncThunk(
  "offerRedeem/redeemOffer",
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;
    try {
      // Build the URL dynamically based on whether selectedMerchant exists
      const baseUrl = "offerRedeem/redeemOffer";
      const withMerchantUrl = (selectedMerchant && selectedMerchant._id)
        ? `${baseUrl}?merchantId=${selectedMerchant._id}`
        : baseUrl;

      try {
        const response = await axios.post(withMerchantUrl, { code: payload?.code });
        return response?.data;
      } catch (err) {
        // If backend says this merchant isn't the owner, retry without merchantId to allow validation
        const msg = err?.response?.data?.message || "";
        const status = err?.response?.status;
        const isOwnerMismatch = status === 403 && /not allowed to redeem this offer because you are not the owner/i.test(msg);
        if (isOwnerMismatch && withMerchantUrl !== baseUrl) {
          try {
            const retry = await axios.post(baseUrl, { code: payload?.code });
            return retry?.data;
          } catch (_) {
            throw err;
          }
        }
        throw err;
      }
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const checkRedemptionCode = createAsyncThunk(
  "offerRedeem/checkCode",
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;
    try {
      const baseUrl = "offerRedeem/checkCode";
      const withMerchantUrl = (selectedMerchant && selectedMerchant._id)
        ? `${baseUrl}?merchantId=${selectedMerchant._id}`
        : baseUrl;

      try {
        const response = await axios.post(withMerchantUrl, { code: payload?.code });
        return response?.data;
      } catch (err) {
        const msg = err?.response?.data?.message || "";
        const status = err?.response?.status;
        const isOwnerMismatch = status === 403 && /not allowed/i.test(msg);
        if (isOwnerMismatch && withMerchantUrl !== baseUrl) {
          try {
            const retry = await axios.post(baseUrl, { code: payload?.code });
            return retry?.data;
          } catch (_) {
            throw err;
          }
        }
        throw err;
      }
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const getVerificationStats = createAsyncThunk(
  "offerRedeem/verificationStats",
  async (_, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const merchantId = resolveMerchantId(state);
      const params = merchantId ? { merchantId } : {};
      const response = await axios.get("offerRedeem/verificationStats", {
        params,
      });
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(getMerchantErrorPayload(error, "Failed to load verification stats"));
    }
  }
);

//Analytics
export const fetchNewCustomerOffers = createAsyncThunk(
  "offerRedeem/newCustomers",
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    const merchantId = resolveMerchantId(state);
    const params = merchantId ? { merchantId } : {};
    try {
      const response = await axios.get("offerRedeem/newCustomers", {
        params,
      });
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(getMerchantErrorPayload(error, "Failed to load customer data"));
    }
  }
);

export const fetchofferRedeemPercentage = createAsyncThunk(
  "offerRedeem/percentage",
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    const merchantId = resolveMerchantId(state);
    const params = merchantId ? { merchantId } : {};
    try {
      const response = await axios.get("offerRedeem/percentage", {
        params,
      });
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(getMerchantErrorPayload(error, "Failed to load redemption percentage"));
    }
  }
);

export const fetchOfferViewsClicks = createAsyncThunk(
  "offer/views/clicks",
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    const merchantId = resolveMerchantId(state);
    const params = merchantId ? { merchantId } : {};
    try {
      const response = await axios.get("offer/views/clicks", {
        params,
      });
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(getMerchantErrorPayload(error, "Failed to load offer metrics"));
    }
  }
);

export const fetchMerchantMostPopularOffer = createAsyncThunk(
  "merchant/mostPopularOffer",
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    const merchantId = resolveMerchantId(state);
    const params = merchantId ? { merchantId } : {};
    try {
      const response = await axios.get("merchant/mostPopularOffer", {
        params,
      });
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(getMerchantErrorPayload(error, "Failed to load popular offer"));
    }
  }
);

export const getFavouriteOffers = createAsyncThunk(
  "merchant/favouriteOffers",
  async (_, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const merchantId = resolveMerchantId(state);
      const params = merchantId ? { merchantId } : {};
      const response = await axios.get("merchant/countOfFavoriteOffers", {
        params,
      });
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(getMerchantErrorPayload(error, "Failed to load favorite offers"));
    }
  }
);

export const getAboutPageViews = createAsyncThunk(
  "merchant/aboutPageViews",
  async (_, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const merchantId = resolveMerchantId(state);
      const params = merchantId ? { merchantId } : {};
      const response = await axios.get("merchant/getAboutPageView", {
        params,
      });
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(getMerchantErrorPayload(error, "Failed to load about page views"));
    }
  }
);

export const getDashboardStats = createAsyncThunk(
  "merchant/stats",
  async (_, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const merchantId = resolveMerchantId(state);
      const params = merchantId ? { merchantId } : {};
      const response = await axios.get("offerRedeem/dashboardStats", {
        params,
      });
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(getMerchantErrorPayload(error, "Failed to load dashboard stats"));
    }
  }
);

export const getRedemptionByDemographics = createAsyncThunk(
  "merchant/demographics",
  async (_, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const merchantId = resolveMerchantId(state);
      const params = merchantId ? { merchantId } : {};
      const response = await axios.get(
        "merchant/getOfferRedemptionByDemographics",
        { params }
      );
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(getMerchantErrorPayload(error, "Failed to load demographics"));
    }
  }
);

export const getRedemptionRates = createAsyncThunk(
  "merchant/redemptionRate",
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    try {
      let url = `offerRedeem/redemptionRate`;
      const params = {};
      const merchantId = resolveMerchantId(state);
      if (merchantId) params.merchantId = merchantId;
      if (payload?.timePeriod) params.timePeriod = payload.timePeriod;
      const queryString = new URLSearchParams(params).toString();
      if (queryString) url += `?${queryString}`;

      const response = await axios.get(url);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(getMerchantErrorPayload(error, "Failed to load redemption rate"));
    }
  }
);

export const getRedeemedFrequencies = createAsyncThunk(
  "merchant/redeemedFrequency",
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    try {
      let url = `offerRedeem/redemptionFrequency`;
      const params = {};
      const merchantId = resolveMerchantId(state);
      if (merchantId) params.merchantId = merchantId;
      if (payload?.timePeriod) params.timePeriod = payload.timePeriod;
      const queryString = new URLSearchParams(params).toString();
      if (queryString) url += `?${queryString}`;

      const response = await axios.get(url);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(getMerchantErrorPayload(error, "Failed to load redemption frequency"));
    }
  }
);

export const getMerchantById = createAsyncThunk(
  "merchant/get",
  async (payload, thunkAPI) => {
    try {
      // Primary admin-safe endpoint for basic merchant data
      const response = await axios.get(`merchant/${payload?.id}`);
      if (response?.data?.success) return response.data;
      
      // Fallback 1: Try business-profile endpoint (often more detailed)
      try {
        const bp = await axios.get(`merchant/business-profile/${payload.id}`);
        if (bp?.data?.success) return bp.data;
      } catch (_) {}

      // Fallback 2: Try profiles endpoint
      try {
        const pr = await axios.get(`merchant/profiles/${payload.id}`);
        if (pr?.data?.success) return pr.data;
      } catch (_) {}

      return response?.data;
    } catch (error) {
      // If primary failed, try the fallbacks one last time in the catch block 
      // to ensure we exhaust all options before rejecting
      try {
        const bp = await axios.get(`merchant/business-profile/${payload?.id}`);
        if (bp?.data?.success) return bp.data;
      } catch (_) {}

      return thunkAPI?.rejectWithValue(error?.response?.data || error);
    }
  }
);

export const approveOrRejectMerchant = createAsyncThunk(
  "admin/updateMerchantStatus",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.post(
        "admin/updateMerchantStatus",
        payload
      );
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

// Delete Merchant (pending only) by id
export const deleteMerchantById = createAsyncThunk(
  "merchant/delete",
  async (payload, thunkAPI) => {
    try {
      // payload: { id }
      // Best-effort cascade: remove all merchant offers before deleting the merchant
      // so we do not leave orphaned offers behind in admin views.
      const merchantId = payload?.id;
      if (!merchantId) {
        throw new Error("Merchant ID is required to delete merchant");
      }
      const offersResponse = await axios.get(`offer/merchant?merchantId=${merchantId}&limit=1000`);
      const offers = offersResponse?.data?.data || offersResponse?.data?.offers || offersResponse?.data || [];
      const offerIds = (Array.isArray(offers) ? offers : [])
        .map((offer) => offer?._id || offer?.id)
        .filter(Boolean);

      for (const offerId of offerIds) {
        await axios.delete(`offer/${offerId}?merchantId=${merchantId}`);
      }

      // Primary endpoint: admin delete-pending
      const response = await axios.patch(`admin/${merchantId}/delete-pending`);
      if (response?.data?.success) return response.data;
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error?.response?.data || error);
    }
  }
);

// Get Merchant Business Profile
export const getMerchantBusinessProfile = createAsyncThunk(
  "merchant/business-profile/get",
  async (payload, thunkAPI) => {
    try {
      // Primary admin-safe endpoint
      const response = await axios.get(`merchant/business-profile/${payload.id}`, {
        headers: { 'Cache-Control': 'no-cache' },
        params: { _: Date.now() },
      });
      if (response?.data?.success === true) {
        return response?.data;
      }
      // Wrap unexpected responses in a proper Error for upstream handlers
      const err = new Error(response?.data?.message || "Failed to fetch business profile");
      err.status = response?.status;
      err.data = response?.data;
      throw err;
    } catch (error) {
      // Fallbacks for environments where RBAC routes differ
      try {
        const alt1 = await axios.get(`merchant/${payload.id}`, {
          headers: { 'Cache-Control': 'no-cache' },
          params: { _: Date.now() },
        });
        if (alt1?.data) return alt1?.data;
      } catch (_) { }
      try {
        const alt2 = await axios.get(`merchant/profile/${payload.id}`, {
          headers: { 'Cache-Control': 'no-cache' },
          params: { _: Date.now() },
        });
        if (alt2?.data) return alt2?.data;
      } catch (_) { }

      return thunkAPI?.rejectWithValue(error?.response?.data || error);
    }
  }
);

// Update Merchant Business Profile
export const updateMerchantBusinessProfile = createAsyncThunk(
  "merchant/business-profile/update",
  async ({ id, data }, thunkAPI) => {
    try {
      const response = await axios.patch(`merchant/business-profile/${id}`, data);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error?.response?.data || error);
    }
  }
);

export const inviteSecondaryMember = createAsyncThunk(
  "merchant/secondary-member/invite",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.post("merchant/account/secondary-member/invite", payload);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error?.response?.data || error);
    }
  }
);

export const resendSecondaryMemberInvite = createAsyncThunk(
  "merchant/secondary-member/resend",
  async (_, thunkAPI) => {
    try {
      const response = await axios.post("merchant/account/secondary-member/resend");
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error?.response?.data || error);
    }
  }
);

export const cancelSecondaryMemberInvite = createAsyncThunk(
  "merchant/secondary-member/cancel",
  async (_, thunkAPI) => {
    try {
      const response = await axios.delete("merchant/account/secondary-member/invite");
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error?.response?.data || error);
    }
  }
);

export const removeSecondaryMember = createAsyncThunk(
  "merchant/secondary-member/remove",
  async (_, thunkAPI) => {
    try {
      const response = await axios.delete("merchant/account/secondary-member");
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error?.response?.data || error);
    }
  }
);

export const getSecondaryMemberInviteDetails = createAsyncThunk(
  "merchant/secondary-member/invite-details",
  async (token, thunkAPI) => {
    try {
      const response = await axios.get(`merchant/secondary-member/invite/${token}`);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error?.response?.data || error);
    }
  }
);

export const acceptSecondaryMemberInvite = createAsyncThunk(
  "merchant/secondary-member/accept",
  async ({ token, payload }, thunkAPI) => {
    try {
      const response = await axios.post(
        `merchant/secondary-member/invite/${token}/accept`,
        payload || {}
      );
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error?.response?.data || error);
    }
  }
);
